import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import { navGroups } from "./navConfig";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

const SIDEBAR_GROUPS_KEY = "sidebar-expanded-groups";

function loadExpandedGroups(): Set<string> {
  try {
    const saved = localStorage.getItem(SIDEBAR_GROUPS_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set<string>();
}

export function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { can } = usePermissions();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(loadExpandedGroups);

  // Pre-filter nav items per group based on permissions
  const visibleNavGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) => !item.requiredPermission || can(item.requiredPermission),
          ),
        }))
        .filter((group) => group.items.length > 0),
    [can],
  );

  // Auto-expand the group containing the active route
  useEffect(() => {
    const activeGroup = visibleNavGroups.find((g) =>
      g.items.some((item) =>
        location.pathname === item.href || location.pathname.startsWith(item.href + "/"),
      ),
    );
    if (activeGroup && !expandedGroups.has(activeGroup.titleKey)) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(activeGroup.titleKey);
        return next;
      });
    }
  }, [location.pathname, visibleNavGroups]);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify([...expandedGroups]));
  }, [expandedGroups]);

  const toggleGroup = useCallback((titleKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(titleKey)) next.delete(titleKey);
      else next.add(titleKey);
      return next;
    });
  }, []);

  const isExpanded = !collapsed || !!mobileOpen;

  const sidebarContent = (
    <aside
      className={`fixed inset-y-0 left-0 z-30 flex flex-col border-r border-gray-200 bg-white transition-all duration-200
        ${collapsed ? "w-16" : "w-60"}
        max-nav:w-60`}
    >
      <div className="flex h-16 items-center justify-between border-b px-4">
        {isExpanded && (
          <Link to="/">
            <img src="/assets/zatio_2018_horizontal_colored.svg" alt="Zatio" className="h-8" />
          </Link>
        )}
        <button onClick={mobileOpen ? onMobileClose : onToggle} title={t("common:actions.menu")} aria-label={t("common:actions.menu")} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {mobileOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-0.5">
        {visibleNavGroups.map((group) => {
          const isGroupExpanded = expandedGroups.has(group.titleKey);
          const groupHasActive = group.items.some((item) =>
            location.pathname === item.href || location.pathname.startsWith(item.href + "/"),
          );
          const groupTitle = t(`common:nav.groups.${group.titleKey}`);
          return (
            <div key={group.titleKey}>
              <button
                type="button"
                onClick={() => toggleGroup(group.titleKey)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                  ${groupHasActive
                    ? "text-brand-dark"
                    : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  }`}
                title={!isExpanded ? groupTitle : undefined}
              >
                <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={group.icon} />
                </svg>
                {isExpanded && (
                  <>
                    <span className="flex-1 text-left">{groupTitle}</span>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform ${isGroupExpanded ? "" : "-rotate-90"}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
              {isExpanded && isGroupExpanded && (
                <ul className="space-y-0.5 ml-4 mt-0.5 border-l border-gray-200 pl-2">
                  {group.items.map((item) => {
                    const isActive =
                      location.pathname === item.href || location.pathname.startsWith(item.href + "/");
                    const itemLabel = t(`common:nav.items.${item.labelKey}`);
                    return (
                      <li key={item.href}>
                        {item.disabled ? (
                          <span
                            className="flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm text-gray-400 cursor-not-allowed"
                            title={t("common:nav.comingSoon")}
                          >
                            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox={item.iconViewBox ?? "0 0 24 24"} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                              {item.iconOverlay && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.iconOverlay} fill="white" />}
                            </svg>
                            <span>{itemLabel}</span>
                          </span>
                        ) : (
                          <Link
                            to={item.href}
                            onClick={onMobileClose}
                            className={`flex items-center gap-3 rounded-lg px-3 py-1.5 text-sm transition-colors
                              ${isActive
                                ? "bg-brand-50 text-brand-dark font-medium"
                                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                              }`}
                          >
                            <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox={item.iconViewBox ?? "0 0 24 24"} stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                              {item.iconOverlay && <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.iconOverlay} fill="white" />}
                            </svg>
                            <span>{itemLabel}</span>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden nav:block">{sidebarContent}</div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="nav:hidden">
          <div className="fixed inset-0 z-20 bg-black/50" onClick={onMobileClose} />
          {sidebarContent}
        </div>
      )}
    </>
  );
}
